import {
  getEbayPositiveReviews,
  getFeaturedEbayReviews,
  ebayReviewToMarketplaceItem,
  EBAY_REVIEWS_NOTE,
} from "./ebayReviews";

/**
 * Marketplace feedback & trust copy.
 * Live positive eBay quotes: see ./ebayReviews.js (scraped snapshot, not invented).
 *
 * @typedef {Object} MarketplaceFeedbackItem
 * @property {string} id
 * @property {string} customerLabel — masked label, e.g. "a***f" or "eBay buyer"
 * @property {string} productName
 * @property {number} [rating] — 1–5, only shown when provided
 * @property {string} comment
 * @property {string} sourceLabel — e.g. "From eBay"
 * @property {string} [dateLabel]
 * @property {string} [verifiedLabel]
 * @property {boolean} featured
 * @property {boolean} approved
 * @property {string[]} [tags]
 */

/** @type {MarketplaceFeedbackItem[]} */
export const MARKETPLACE_FEEDBACK = [
  // Live positive eBay feedback lives in ebayReviews.js (carousel + wall).
  // Optional manual drafts can still be added below with approved: false.
];

export const MARKETPLACE_TRUST_NOTE = EBAY_REVIEWS_NOTE;

export const REVIEWS_DISCLAIMER =
  "Personal routines only, not medical advice. We do not claim clinically proven, guaranteed, or cure results.";

/** @param {MarketplaceFeedbackItem[]} list */
function sortFeaturedFirst(list) {
  return [...list].sort((a, b) => Number(b.featured) - Number(a.featured));
}

/** Approved marketplace feedback only (approved: false stays off the live site). */
export function getMarketplaceFeedback() {
  const fromEbay = getEbayPositiveReviews().map(ebayReviewToMarketplaceItem);
  const manual = MARKETPLACE_FEEDBACK.filter((r) => r.approved === true);
  const byId = new Map();
  [...fromEbay, ...manual].forEach((item) => {
    byId.set(item.id, item);
  });
  return sortFeaturedFirst([...byId.values()]);
}

/** Homepage: up to `max` featured substantive eBay quotes. */
export function getFeaturedMarketplaceFeedback(max = 3) {
  const featured = getFeaturedEbayReviews(max).map(ebayReviewToMarketplaceItem);
  if (featured.length) return featured;
  return getMarketplaceFeedback().slice(0, max);
}

/** @deprecated Use MARKETPLACE_FEEDBACK — legacy alias */
export const EBAY_REVIEW_HIGHLIGHTS = MARKETPLACE_FEEDBACK;

/** @deprecated Use getMarketplaceFeedback */
export function getApprovedEbayReviews() {
  return getMarketplaceFeedback();
}

/** @deprecated Use getFeaturedMarketplaceFeedback */
export function getHomepageEbayHighlights(max = 3) {
  return getFeaturedMarketplaceFeedback(max);
}

/**
 * Website stories / editorial voice — separate from marketplace feedback.
 * @deprecated Not customer reviews; use /stories for published website voices.
 */
export const WEBSITE_VOICE_TESTIMONIALS = [
  {
    quote:
      "The blender moment sold me. I could actually see my herbs drop in. Gel arrived cold and thick, not watery.",
    name: "Amelia · London",
    tag: "Custom 500 ml",
  },
  {
    quote: "I rotate gold and purple in oats and cooled tea. No weird aftertaste, just a mineral note that feels grown-up.",
    name: "Jordan · Bristol",
    tag: "Gold + Purple",
  },
  {
    quote:
      "Finally a brand that does not promise to cure my entire personality. Just good gel and clear storage tips.",
    name: "Sam · Manchester",
    tag: "1 Litre jar",
  },
  {
    quote:
      "The jar label looked like something I would leave out on a shelf. I was proud to gift a second bottle to my mum.",
    name: "Priya · Leeds",
    tag: "Gift order",
  },
];
